import React, { useState, useEffect } from "react"
import clsx from "clsx"
import {
	IconButton,
	Menu,
	MenuItem,
	ListItemText,
	Typography,
	Grid,
	Tooltip,
	SvgIconProps,
	Divider as MuiDivider,
	ListItemIcon
} from "@material-ui/core"

import {
	TextEnhancementIcon,
	GeneralTextImprovementIcon,
	ConfidentToneIcon,
	FriendlyToneIcon,
	ProfessionalToneIcon,
	DirectToneIcon,
	EmpatheticToneIcon
} from "@/assets/icons"

import { Divider, SvgIcon, UpsellDialog } from "@/components"

import TextEnhancementDialog, { TextEnhancementDialogProps } from "@/components/Skynet/TextEnhancementDialog"

import { isLastItem } from "@/utils/array"

import { colors } from "@/styles/colors"

import { EnhanceData, EnhanceType } from "@/protocols/skynet"

import useCustomMemo from "@/hooks/useCustomMemo"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import AnalyticsService from "@/services/Analytics"

import useStyles from "@/components/Skynet/TextEnhancementButton/styles"
import UpsellHandlerDialog from "@/components/UpsellDialog/UpsellHandlerDialog"
import TextEnhancementUsageService from "@/services/TextEnhancementUsage"

type MenuConfigItem<Type extends EnhanceType>= {
	icon?: React.ReactNode
	title: React.ReactNode
	enhanceData: EnhanceData<Type>
	TextEnhancementDialogProps?: Pick<TextEnhancementDialogProps<EnhanceType>, "secondaryTitleText">
}

type MenuConfig<Type extends EnhanceType> = {
	title?: React.ReactNode
	items: MenuConfigItem<Type>[]
}

type EnhanceTypeToMenuConfig<Type extends EnhanceType> = Record<Type, MenuConfig<Type>>

type TextEnhancementButtonProps = {
	TextEnhancementDialogProps: Pick<TextEnhancementDialogProps<EnhanceType>, "anchorElement" | "customSecondaryTitleTextStyle">
	IconButtonSvgIconProps?: SvgIconProps
	text: string
	onReplace: (text: string) => void
}

type AITextEnhancementActionDialogType = "show-simple-upsell-dialog" | "show-automatic-upsell-dialog" | "show-text-enhancement-dialog"

const DEFAULT_MENU_ICON_COLOR = colors.purple[500]

const ENHANCE_TYPE_TO_ANALYTICS_EVENT: Record<EnhanceType, string> = {
	"generic-text-improvement": "ai_text_improvement_clicked",
	"tone-switch": "ai_content_variation_clicked"
}

const TextEnhancementButton: React.FC<TextEnhancementButtonProps> = (props) => {
	const {
		TextEnhancementDialogProps,
		IconButtonSvgIconProps,
		onReplace,
		text
	} = props

	const classes = useStyles()
	const aiTextEnhancementQuantityLimit = useSubscriptionLimits("quantity", "ai_text_enhancement")
	const aiResourcesPermission = useSubscriptionLimits("permission", "ai_resources")
	const automaticUpsellPermission = useSubscriptionLimits("permission", "can_make_automatic_upsell")

	const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null)
	const [shouldPulse, setShouldPulse] = useState(false)

	useEffect(() => {
		let isMounted = true

		TextEnhancementUsageService.fetchRecentUsage().then(({ hasRecentUsage }) => {
			if (isMounted) {
				setShouldPulse(!hasRecentUsage)
			}
		})

		const unsubscribe = TextEnhancementUsageService.subscribeToUsageChange(() => {
			setShouldPulse(false)
		})

		return () => {
			isMounted = false
			unsubscribe()
		}
	}, [])

	const ENHANCE_TYPE_TO_MENU_CONFIG: EnhanceTypeToMenuConfig<EnhanceType> = useCustomMemo(() => ({
		"generic-text-improvement": {
			items: [
				{
					icon: (
						<SvgIcon
							autoSize
							htmlColor={DEFAULT_MENU_ICON_COLOR}
							icon={GeneralTextImprovementIcon}
						/>
					),
					title: "Melhorar o texto",
					enhanceData: {
						text
					},
					TextEnhancementDialogProps: {
						secondaryTitleText: "Melhoria no texto"
					}
				}
			]
		},
		"tone-switch": {
			title: "Mudar o tom do texto",
			items: [
				{
					icon: (
						<SvgIcon
							autoSize
							htmlColor={DEFAULT_MENU_ICON_COLOR}
							icon={FriendlyToneIcon}
						/>
					),
					title: "Soar amigável",
					enhanceData: {
						text,
						tone: "friendly"
					},
					TextEnhancementDialogProps: {
						secondaryTitleText: (
							<Grid
								container
								alignContent="center"
							>
								<SvgIcon
									autoSize
									fontSize="small"
									htmlColor={DEFAULT_MENU_ICON_COLOR}
									icon={FriendlyToneIcon}
								/>

								<Divider orientation="vertical" size={0.5} />

								Tom amigável
							</Grid>
						)
					}
				},
				{
					icon: (
						<SvgIcon
							autoSize
							htmlColor={DEFAULT_MENU_ICON_COLOR}
							icon={ConfidentToneIcon}
						/>
					),
					title: "Soar confiante",
					enhanceData: {
						text,
						tone: "confident"
					},
					TextEnhancementDialogProps: {
						secondaryTitleText: (
							<Grid
								container
								alignContent="center"
							>
								<SvgIcon
									autoSize
									fontSize="small"
									htmlColor={DEFAULT_MENU_ICON_COLOR}
									icon={ConfidentToneIcon}
								/>

								<Divider orientation="vertical" size={0.5} />

								Tom confiante
							</Grid>
						)
					}
				},
				{
					icon: (
						<SvgIcon
							autoSize
							htmlColor={DEFAULT_MENU_ICON_COLOR}
							icon={DirectToneIcon}
						/>
					),
					title: "Soar direto",
					enhanceData: {
						text,
						tone: "direct"
					},
					TextEnhancementDialogProps: {
						secondaryTitleText: (
							<Grid
								container
								alignContent="center"
							>
								<SvgIcon
									autoSize
									fontSize="small"
									htmlColor={DEFAULT_MENU_ICON_COLOR}
									icon={DirectToneIcon}
								/>

								<Divider orientation="vertical" size={0.5} />

								Tom direto
							</Grid>
						)
					}
				},
				{
					icon: (
						<SvgIcon
							autoSize
							htmlColor={DEFAULT_MENU_ICON_COLOR}
							icon={EmpatheticToneIcon}
						/>
					),
					title: "Soar empático",
					enhanceData: {
						text,
						tone: "empathetic"
					},
					TextEnhancementDialogProps: {
						secondaryTitleText: (
							<Grid
								container
								alignContent="center"
							>
								<SvgIcon
									autoSize
									fontSize="small"
									htmlColor={DEFAULT_MENU_ICON_COLOR}
									icon={EmpatheticToneIcon}
								/>

								<Divider orientation="vertical" size={0.5} />

								Tom empático
							</Grid>
						)
					}
				},
				{
					icon: (
						<SvgIcon
							autoSize
							htmlColor={DEFAULT_MENU_ICON_COLOR}
							icon={ProfessionalToneIcon}
						/>
					),
					title: "Soar profissional",
					enhanceData: {
						text,
						tone: "professional"
					},
					TextEnhancementDialogProps: {
						secondaryTitleText: (
							<Grid
								container
								alignContent="center"
							>
								<SvgIcon
									autoSize
									fontSize="small"
									htmlColor={DEFAULT_MENU_ICON_COLOR}
									icon={ProfessionalToneIcon}
								/>

								<Divider orientation="vertical" size={0.5} />

								Tom profissional
							</Grid>
						)
					}
				}
			]
		}
	}), [text])

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElement(event.currentTarget)
	}

	const handleCloseMenu = () => {
		setAnchorElement(null)
	}

	const reloadAiTextEnhancementSubscriptionLimit = () => {
		aiTextEnhancementQuantityLimit.resetLimit()
	}

	const handleOpenTextEnhancementDialog = (
		enhanceType: EnhanceType,
		enhanceData: EnhanceData<EnhanceType>,
		textEnhancementDialogProps?: Partial<TextEnhancementDialogProps<EnhanceType>>
	) => {
		const aiTextEnhancementActionDialogTypeToOpenAITextEnhancementActionDialogFunction: Record<AITextEnhancementActionDialogType, () => void> = {
			"show-simple-upsell-dialog": () => (
				UpsellDialog.open({
					dialogCode: aiTextEnhancementQuantityLimit.blockCode
				})
			),
			"show-automatic-upsell-dialog": () => (
				UpsellHandlerDialog.open({
					type: "ai-text-enhancement-limit-reached-admin"
				})
			),
			"show-text-enhancement-dialog": () => {
				AnalyticsService.capture(ENHANCE_TYPE_TO_ANALYTICS_EVENT[enhanceType])
				TextEnhancementUsageService.notifyUsageDetected()

				TextEnhancementDialog.open({
					anchorElement: TextEnhancementDialogProps.anchorElement,
					enhanceType,
					enhanceData,
					onReplace,
					onTextEnhanced: () => {
						TextEnhancementUsageService.notifyUsageDetected()
						reloadAiTextEnhancementSubscriptionLimit()
					},
					customSecondaryTitleTextStyle: TextEnhancementDialogProps.customSecondaryTitleTextStyle,
					...textEnhancementDialogProps
				})
			}
		}

		const hasFreeAITextEnhancementUsages = !aiTextEnhancementQuantityLimit.mustBlock
		const hasAICreditsPlanAddon = !aiResourcesPermission.mustBlock

		const mustShowUpsellDialog = !hasFreeAITextEnhancementUsages && !hasAICreditsPlanAddon

		const isAdminUserBlockCode = aiTextEnhancementQuantityLimit.blockCode === "ai-text-enhancement-limit-reached-admin"

		const canShowAutomaticUpsellDialog = mustShowUpsellDialog && isAdminUserBlockCode && !automaticUpsellPermission.mustBlock
		const canShowSimpleUpsellDialog = mustShowUpsellDialog && !canShowAutomaticUpsellDialog

		let aiTextEnhancementActionDialogType: AITextEnhancementActionDialogType

		if (canShowSimpleUpsellDialog) {
			aiTextEnhancementActionDialogType = "show-simple-upsell-dialog"
		} else if (canShowAutomaticUpsellDialog) {
			aiTextEnhancementActionDialogType = "show-automatic-upsell-dialog"
		} else {
			aiTextEnhancementActionDialogType = "show-text-enhancement-dialog"
		}

		const openAiTextEnhancementActionDialog = aiTextEnhancementActionDialogTypeToOpenAITextEnhancementActionDialogFunction[aiTextEnhancementActionDialogType]

		handleCloseMenu()

		return openAiTextEnhancementActionDialog()
	}

	const enhanceTypeAndMenuConfig = Object.entries(ENHANCE_TYPE_TO_MENU_CONFIG).map(([key, value]) => ({
		enhanceType: key as EnhanceType,
		menuConfig: value
	}))

	return (
		<>
			<Menu
				anchorEl={anchorElement}
				open={Boolean(anchorElement)}
				onClose={handleCloseMenu}
				autoFocus={false}
			>
				{enhanceTypeAndMenuConfig.map(({ enhanceType, menuConfig }, index) => (
					(
						<>
							{menuConfig.title && (
								<Grid
									key={`${enhanceType}-title`}
									className={classes.menuItemTitle}
									container
									alignContent="center"
								>
									<Typography
										variant="overline"
									>
										{menuConfig.title}
									</Typography>
								</Grid>
							)}

							{menuConfig.items.map((item, itemIndex) => (
								<MenuItem
									key={`${enhanceType}-menu-item-${itemIndex}`}
									onClick={() => handleOpenTextEnhancementDialog(enhanceType, item.enhanceData, item.TextEnhancementDialogProps)}
								>
									{item.icon && (
										<ListItemIcon
											className={classes.listItemIcon}
										>
											{item.icon}
										</ListItemIcon>
									)}

									<ListItemText
										primary={item.title}
										classes={{
											primary: classes.listItemText
										}}
									/>
								</MenuItem>
							))}

							{!isLastItem(index, enhanceTypeAndMenuConfig.length) && (
								<MuiDivider
									key={`${enhanceType}-divider`}
								/>
							)}
						</>
					)
				))}
			</Menu>

			<Tooltip
				title="Melhore seu texto, crie novas possibilidades e muito mais"
			>
				<IconButton
					onClick={handleOpenMenu}
					size="small"
				>
					<SvgIcon
						{...IconButtonSvgIconProps}
						className={clsx(IconButtonSvgIconProps?.className, shouldPulse && classes.pulseAnimation)}
						htmlColor={colors.purple[500]}
						icon={TextEnhancementIcon}
					/>
				</IconButton>
			</Tooltip>
		</>
	)
}

export default TextEnhancementButton
